import * as React from 'react';
import ReactHtmlParser, { processNodes } from 'react-html-parser';
import { connect } from 'react-redux';
import fetch from 'cross-fetch';
import * as Handlebars from 'handlebars';

import { AjaxCallHelpers } from '../formcontrols/helpers/AjaxCallHelpers';
import { IModule } from '../modules/module';
import { ReactFormHelpers } from '../containers/helpers/FormHelpers';
import { registerHandlebarsHelpers } from '../../helpers/handlebars-helpers';
import { StringHelpers } from '../helpers/StringHelpers';

import { IFormConfig } from '../interfaces/FormConfig';
import { retrieveSavedQuerystring } from '../../helpers/sessionstorage-helpers';

registerHandlebarsHelpers();

export interface RichtTextContentProps {
  forms: any;
  formConfig: IFormConfig;
  formFieldDefinition: any;
  formName: string;
  payload: any;
  formDefinitions: any;
  formController: any;
  formikBag: any;  
}

export interface RichtTextContentState {
  module: IModule;
  isLoadingModule: boolean;
  isLoading: boolean;
  isErrorLoading: boolean;
  collapseUpdate: number;
}

const mapStateToProps = (state: any) => {
  return {
      forms: state.forms
  };
};

class InternalRichtTextContent extends React.Component<RichtTextContentProps, RichtTextContentState> {
  constructor(props: RichtTextContentProps) {
    super(props);
    this.state = {
        module: undefined,
        isLoadingModule: false,
        isLoading: false,
        isErrorLoading: false,
        collapseUpdate: 1
    };
  }

  componentDidMount() {
    if (this.props.formFieldDefinition.useModule) {
      this.setState({isLoadingModule: true});
      import('../modules/' + this.props.formFieldDefinition.useModule).then(module => {    
        if (this.unmounted) {
          return;
        }  
        this.setState(
          {
            module: new module.Module(),
            isLoadingModule: false
          });
      }); 
    }

    if (this.props.formFieldDefinition.triggerDatasource) {
      AjaxCallHelpers.getRelatedData(this as any, "");
    }
  }

  unmounted : boolean = false;

  componentWillUnmount() {
      this.unmounted = true;
  }

  showPDF(blobInfo) {
    if (!!blobInfo.error) {
      return;
    }
    if (blobInfo && blobInfo.blob) {
      blobInfo.blob.then(blobData => {
    
        let newBlob = new Blob([blobData], {type: "application/pdf"});
    
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(newBlob);
          return;
        } 
      
        const data = window.URL.createObjectURL(newBlob);
        let link = document.createElement('a');
        link.href = data;
        link.target = "_blank";
        link.download = blobInfo.fileName || "file.pdf";
        link.click();
        setTimeout(function(){
          // For Firefox it is necessary to delay revoking the ObjectURL
          window.URL.revokeObjectURL(data);
        }, 100);
      });
    }
  }

  onClick = (href, event) => {
    let data = {};

    if (!!this.props.formFieldDefinition.includePayload) {
      data = {...data, ...this.props.payload};
    }

    // fill data from current form using form definition to prevent prefilled data to be inserted:
    let currentFormData = {};
    let currentForm = this.props.forms[this.props.formName];
    if (currentForm) {
      this.props.formDefinitions[this.props.formName].map((fieldDef) => {
        if (currentForm[fieldDef.name]) {
          currentFormData[fieldDef.name] = currentForm[fieldDef.name]; 
        }
      });
    }
    data = {...data, ...currentFormData};
    
    this.props.formConfig.formSteps.map((form: string, index) => {
      // ignore current screen, this is filled with values from current form in the code above
      if (form != this.props.formName) {        
        if (this.props.forms[form] !== undefined) {
            data = (Object as any).assign({}, data, this.props.forms[form]);
        }
      } 
    });

    data = ReactFormHelpers.rewriteValueObjectsInPostData(data, this.props.formDefinitions, this.props.formConfig.formSteps);
    
    fetch(
        href,
        {
            method: 'post',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        }
    )
    .then(response => {
      if (response.status >= 300) {
        return {
          error: true 
        };
      }
      let contentType = response.headers.get('content-type');
      if (contentType == "application/pdf") {
        let disposition = response.headers.get('content-disposition');
        let fileName = disposition.match(/filename=(.+)/)[1];
        return {
          fileName : fileName,
          blob: response.blob()
        };
      } else {
        return {
          error: true 
        };
      }
    })
    .then(blob => this.showPDF(blob));
  }

  onCollapse = (collapseId : string, event) => {
    event.preventDefault();
    this.setState(state => {
      return {...state, 
        collapseUpdate: state.collapseUpdate + 1,
        [collapseId] : !state[collapseId]
      };
    });
    return false;
  }

  transformNode = (node, index) => {
    if (node.type === 'tag' && node.name === 'a') {
      if (node && node.attribs) {
        // PBI#25634 - need to support querystring parameters in URLS between different forms
        if (this.props.formConfig.usePersistedQueryString &&
          node.attribs['href'].length > 0){ 
          node.attribs['href'] = `${node.attribs['href']}?${retrieveSavedQuerystring()}`;
        }
        if (!!node.attribs["data-include-form-data"]  || 
          node.attribs["data-include-form-data"] == "true") {
          return <a key={"transform-node-" + index} target={node.attribs["target"] || ""} 
            onClick={this.onClick.bind(this, node.attribs["href"])} 
            className={node.attribs["class"]}>{processNodes(node.children)}</a>;
        } 
        else if(!!node.attribs["data-collapseid"]) {
          return <a key={"transform-node-" + index} target={node.attribs["target"] || ""} 
            onClick={this.onCollapse.bind(this, node.attribs["data-collapseid"])} 
            className={node.attribs["class"]}>{processNodes(node.children)}</a>;
        }
      }
    } 
    if (node.type === 'tag' && node.name === 'div') {
      if (node && node.attribs) {      
        if (node.attribs["data-collapsableid"]) {
          return <div key={"transform-node-" + index}                    
            className={(node.attribs["class"] || "") + " collapsible-content " + node.attribs["data-collapsableid"] + " " + 
            (!!this.state[node.attribs["data-collapsableid"]] ? "" : "hidden")}>{processNodes(node.children)}</div>;
        }
      }
    }
  }


  render() {
    let data = {};

    if (this.props.formFieldDefinition.form) {
      data = {...this.props.forms[this.props.formFieldDefinition.form],
        ...this.props.forms[this.props.formName]};
    } else {
      data = {...this.props.forms[this.props.formName]};
    }

    if (!!this.props.formFieldDefinition.includePayload) {
      data = {...data, ...this.props.payload};
    }

    let html = this.props.formFieldDefinition.content || "";
    if (this.props.formFieldDefinition.templateType === "handlebars") {
      const template = Handlebars.compile(this.props.formFieldDefinition.content || "");
      html = template({...data, ...this.props.formikBag.values});
    }

    let contentHtml = ReactHtmlParser(StringHelpers.replacePropertiesInString(html, 
      data, true), {
        transform: this.transformNode
      });
      
      
    return <>
      {(!!this.state.isLoadingModule || !!this.state.isLoading) && <div className="pb-3">
        <span className="spinner-border"></span>
      </div>}
      {!!this.state.isErrorLoading && (this.props.formController.props.formConfig as any).ErrorOnGetRelatedData && 
        <div className="container form-richtextcontent-block form__invalid-feedback font-weight-bold">{ReactHtmlParser((this.props.formController.props.formConfig as any).ErrorOnGetRelatedData || "")}</div>}
      {!this.state.isLoading && !this.state.isErrorLoading && <div className="container form-richtextcontent-block" data-collapse={this.state.collapseUpdate}>            
          {contentHtml}
      </div>}
      {!!this.props.formFieldDefinition.useModule && this.state.module && this.state.module.render(this.props.forms)}
    </>;
  }
}

const RichtTextContent = connect(mapStateToProps)(InternalRichtTextContent);

export { RichtTextContent };